import React, { useState, useEffect, useRef } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  TextField,
  Button,
  List,
  ListItem,
  ListItemText,
  Box,
  Typography,
  CircularProgress,
} from "@mui/material";
import ArrowBackIosIcon from "@mui/icons-material/ArrowBackIos";
import Sortable from "sortablejs";
import { ZodiacEnum } from "../../../../../constants/enum";
import { text_field_styles } from "../../../../../constants/style";
import { ENDPOINTS, getApiUrl } from "../../../../../common/endpoints";
import { Requests } from "../../../../../common/requests";
import { useSnackbar } from "notistack";

export interface DataZodiacRanking {
  id?: string;
  name: string;
  ranking: ZodiacEnum[];
}
interface FormRankingModalProps {
  open: boolean;
  onClose: (isRefresh?: boolean) => void;
  ranking?: DataZodiacRanking | null;
  isEditMode: boolean;
}

const FormRankingModal: React.FC<FormRankingModalProps> = ({
  open,
  onClose,
  ranking,
  isEditMode,
}) => {
  const { enqueueSnackbar } = useSnackbar();
  const initialFormData: DataZodiacRanking = ranking
    ? {
        id: ranking.id,
        name: ranking.name,
        ranking: ranking.ranking,
      }
    : {
        name: "",
        ranking: [
          ZodiacEnum.Aries,
          ZodiacEnum.Taurus,
          ZodiacEnum.Gemini,
          ZodiacEnum.Cancer,
          ZodiacEnum.Leo,
          ZodiacEnum.Virgo,
          ZodiacEnum.Libra,
          ZodiacEnum.Scorpio,
          ZodiacEnum.Sagittarius,
          ZodiacEnum.Capricorn,
          ZodiacEnum.Aquarius,
          ZodiacEnum.Pisces,
        ],
      };

  const [formData, setFormData] = useState<DataZodiacRanking>(initialFormData);
  const sortableRef = useRef<HTMLUListElement | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (ranking) {
      setFormData({
        id: ranking.id,
        name: ranking.name,
        ranking: ranking.ranking,
      });
    }
  }, [ranking]);

  useEffect(() => {
    if (sortableRef.current) {
      const sortable = Sortable.create(sortableRef.current, {
        onEnd: (event: any) => {
          const newRanking = [...formData.ranking];
          const [removed] = newRanking.splice(event.oldIndex!, 1);
          newRanking.splice(event.newIndex!, 0, removed);

          setFormData({
            ...formData,
            ranking: newRanking,
          });
        },
      });

      return () => sortable.destroy();
    }
  }, [formData.ranking]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleSubmit = async () => {
    setIsLoading(true);
    try {
      if (isEditMode) {
        const apiUrl = getApiUrl(
          ENDPOINTS.ZODIAC_RANKING_EDIT.replace(":id", formData?.id ?? "")
        );
        await Requests.put(apiUrl, formData);
      } else {
        const apiUrl = getApiUrl(ENDPOINTS.ZODIAC_RANKING_ADD);
        await Requests.post(apiUrl, formData);
      }
      enqueueSnackbar("Save successfully!", { variant: "success" });
      onClose(true);
    } catch (e) {
      enqueueSnackbar("Save unsuccessfully!", { variant: "error" });
    }
    setIsLoading(false);
  };

  return (
    <Box
      sx={{
        padding: "10px",
      }}
    >
      <Box>
        <Button
          onClick={() => onClose()}
          color="primary"
          sx={{
            textTransform: "unset",
            fontWeight: "bold",
          }}
          startIcon={<ArrowBackIosIcon />}
        >
          Back
        </Button>
      </Box>
      <Box
        sx={{
          height: "calc(100vh - 120px)",
          overflowY: "auto",
        }}
      >
        <TextField
          label="Name"
          name="name"
          value={formData.name}
          onChange={handleChange}
          fullWidth
          margin="normal"
          sx={{
            ...text_field_styles,
          }}
        />
        <Typography
          sx={{
            color: "#2A4365",
            fontWeight: "bold",
            fontSize: "14px",
          }}
        >
          Ranking
        </Typography>
        <List ref={sortableRef}>
          {formData.ranking.map((sign: any) => (
            <ListItem
              key={sign}
              sx={{
                marginBottom: "4px",
                padding: "4px 8px",
                backgroundColor: "#fff",
                border: "1px solid #ccc",
                borderRadius: "4px",
                cursor: "move",
                color: "#2A4365",
                fontSize: "14px",
                "&:hover": {
                  "& span": { fontWeight: "bold" },
                },
              }}
            >
              <ListItemText primary={sign} />
            </ListItem>
          ))}
        </List>
      </Box>

      <Box
        sx={{
          display: "flex",
          justifyContent: "flex-end",
          paddingTop: "10px",
        }}
      >
        <Button
          variant="contained"
          color="secondary"
          sx={{ textTransform: "unset", marginRight: "10px" }}
          onClick={() => onClose()}
          disabled={isLoading}
        >
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          color="primary"
          variant="contained"
          sx={{ textTransform: "unset" }}
          disabled={isLoading}
        >
          {isLoading && (
            <CircularProgress
              size="30px"
              sx={{
                marginRight: "10px",
                color: "#fff",
              }}
            />
          )}
          {isEditMode ? "Update" : "Create"}
        </Button>
      </Box>
    </Box>
  );
};

export default FormRankingModal;
